import { LEGAL_DOCUMENT_PATHS, LEGAL_VERSION, type LegalDocumentId } from '@falasta/contracts';

/**
 * Termini di servizio, informativa privacy e cookie policy, come dati.
 *
 * Sono testi che vengono **accettati**: la versione che l'API archivia insieme
 * all'account (`LEGAL_VERSION`) è la versione di *questo* file. Da qui due
 * conseguenze pratiche:
 *
 *  1. modificare la sostanza di un documento senza alzare `LEGAL_VERSION` in
 *     `@falasta/contracts` significa avere consensi che puntano a un testo che
 *     nessuno ha letto — `legal-content.spec.ts` fallisce se accade, perché
 *     confronta un'impronta del contenuto con quella registrata qui sotto;
 *  2. correzioni di forma (una virgola, un refuso) non richiedono una nuova
 *     versione: in quel caso si aggiorna l'impronta e si lascia la versione dov'è,
 *     dichiarandolo nel messaggio del commit.
 *
 * Il testo sta nella copertina e non nei contratti perché è **contenuto**: è
 * questo applicativo che lo pubblica, e i contratti non devono trasportare
 * ottomila caratteri di italiano dentro il bundle di ogni servizio.
 */

export interface LegalSection {
  readonly id: string;
  readonly heading: string;
  readonly paragraphs: readonly string[];
  readonly bullets?: readonly string[];
}

export interface LegalDocument {
  readonly id: LegalDocumentId;
  /** Percorso su cui è pubblicato, lo stesso che i contratti dichiarano. */
  readonly path: string;
  readonly title: string;
  /** Una riga di sintesi: compare sotto il titolo e nel piede della copertina. */
  readonly summary: string;
  readonly sections: readonly LegalSection[];
}

/**
 * Il titolare del trattamento.
 *
 * Falasta è offerto da una persona fisica a titolo personale e gratuito: non è
 * un'impresa, non c'è partita IVA da citare, e questo va detto nei documenti
 * perché cambia le aspettative di chi si registra.
 */
export const LEGAL_HOLDER = {
  name: 'Carlo Romano',
  email: 'carlo.romano1981@gmail.com',
  description: 'persona fisica, a titolo personale e non professionale',
} as const;

/** Le età minime dichiarate nei termini, in un posto solo: le frasi le interpolano. */
export const LEGAL_AGE_LIMITS = { admin: 18, participant: 14 } as const;

/** Giorni entro cui si risponde a una richiesta sui dati (art. 12 GDPR: un mese). */
export const LEGAL_RESPONSE_DAYS = 30;

export const LEGAL_EFFECTIVE_DATE = LEGAL_VERSION;

const TERMS: LegalDocument = {
  id: 'terms',
  path: LEGAL_DOCUMENT_PATHS.terms,
  title: 'Termini di servizio',
  summary: 'Che cosa è Falasta, che cosa non è, e a quali condizioni si usa.',
  sections: [
    {
      id: 'chi-offre',
      heading: '1. Chi offre il servizio',
      paragraphs: [
        `Falasta è un servizio gratuito offerto da ${LEGAL_HOLDER.name} (${LEGAL_HOLDER.description}), raggiungibile all'indirizzo ${LEGAL_HOLDER.email}.`,
        'Non è un servizio professionale né un prodotto commerciale: non esiste un contratto di assistenza, non sono garantiti tempi di ripristino e non è previsto alcun corrispettivo. Usandolo accetti queste condizioni; se non le accetti, non registrarti e non partecipare a un’asta.',
      ],
    },
    {
      id: 'cosa-e',
      heading: '2. Che cosa fa Falasta',
      paragraphs: [
        'Falasta conduce l’asta di una lega di fantacalcio: chiama i calciatori uno alla volta, cronometra i rilanci, aggiudica allo scadere e tiene il conto dei crediti. È uno strumento di supporto a un gioco fra amici.',
        'Le liste dei calciatori non sono fornite da Falasta: le carica l’amministratore dell’asta, in un file che resta sotto la sua responsabilità.',
      ],
    },
    {
      id: 'nessuna-affiliazione',
      heading: '3. Nessun legame con la Lega Serie A o con Fantacalcio®',
      paragraphs: [
        'Falasta è un progetto indipendente. Non è affiliato, sponsorizzato, approvato né in alcun modo collegato alla Lega Serie A, alla FIGC, a Panini S.p.A., al marchio Fantacalcio® o ad alcuna società calcistica.',
        'I nomi di squadre, calciatori e competizioni eventualmente presenti sono marchi o dati dei rispettivi titolari e compaiono solo perché caricati dagli utenti a fini descrittivi.',
      ],
    },
    {
      id: 'niente-denaro',
      heading: '4. Nessun denaro, nessuna scommessa',
      paragraphs: [
        'I «fantamilioni» sono unità di conto interne al gioco: non hanno alcun valore economico, non sono acquistabili, non sono convertibili e non rappresentano crediti di alcun tipo.',
        'È vietato usare Falasta per raccogliere quote di partecipazione, assegnare premi in denaro, organizzare scommesse o qualunque forma di gioco con vincita in denaro. Chi lo fa viola questi termini e ne risponde personalmente, anche nei confronti delle autorità competenti.',
      ],
    },
    {
      id: 'account',
      heading: '5. Account, età e credenziali',
      paragraphs: [
        `L’account amministratore è riservato a chi ha compiuto ${String(LEGAL_AGE_LIMITS.admin)} anni: chi organizza un’asta inserisce i dati di altre persone, e questa è una responsabilità che non si può chiedere a un minore.`,
        `Per partecipare a un’asta su invito è richiesta un’età minima di ${String(LEGAL_AGE_LIMITS.participant)} anni. Se inviti un minore, dichiari di avere l’autorizzazione di chi ne esercita la responsabilità genitoriale.`,
        'Sei responsabile della custodia della tua password e di tutto ciò che avviene dalla tua sessione. Il link di invito che ricevi per email vale come una chiave: chi lo possiede entra nella tua asta, quindi non condividerlo.',
      ],
    },
    {
      id: 'dati-dei-partecipanti',
      heading: '6. I dati dei fantallenatori che inviti',
      paragraphs: [
        'Per invitare un fantallenatore inserisci il suo nome e la sua email. Facendolo dichiari e garantisci di essere legittimato a comunicarli e di averlo informato che i suoi dati saranno trattati per lo svolgimento dell’asta, indicandogli l’informativa privacy pubblicata su questo sito.',
        'Se qualcuno contesta l’uso dei propri dati perché non era stato informato o non aveva acconsentito, la responsabilità è di chi li ha inseriti. Ti impegni a tenere indenne il titolare da pretese, sanzioni e spese che derivino dalla violazione di questa garanzia.',
        'Non inserire dati che non ti servono: per un’asta bastano un nome riconoscibile e un’email valida.',
      ],
    },
    {
      id: 'uso-corretto',
      heading: '7. Uso corretto',
      paragraphs: ['Non è consentito, in particolare:'],
      bullets: [
        'usare nomi, testi o file che siano offensivi, discriminatori, diffamatori o illeciti;',
        'tentare di accedere ad aste, account o dati che non ti appartengono;',
        'automatizzare le offerte, sondare il servizio con script o generare carichi anomali;',
        'copiare, decompilare o rivendere il servizio o parti di esso;',
        'usare Falasta per inviare comunicazioni non richieste a indirizzi email di terzi.',
      ],
    },
    {
      id: 'come-e',
      heading: '8. Il servizio è fornito «così com’è»',
      paragraphs: [
        'Falasta viene offerto nello stato in cui si trova, senza garanzie di continuità, di assenza di errori o di idoneità a uno scopo particolare. Un’asta può interrompersi per un guasto della rete, del server o di un servizio di terzi.',
        'Non è garantita la conservazione dei dati né l’esistenza di copie di sicurezza recuperabili. Se un’asta ti serve come documento, esportala quando è finita.',
      ],
    },
    {
      id: 'responsabilita',
      heading: '9. Limitazione di responsabilità',
      paragraphs: [
        'Nei limiti consentiti dalla legge, il titolare non risponde di danni indiretti, perdita di dati, mancato guadagno, interruzione dell’attività o pregiudizi derivanti dall’esito di un’asta, dalle decisioni prese durante il gioco o dall’indisponibilità del servizio.',
        'Resta ferma la responsabilità per dolo e colpa grave, e restano fermi i diritti inderogabili riconosciuti ai consumatori dalla legge italiana.',
      ],
    },
    {
      id: 'sospensione',
      heading: '10. Sospensione e chiusura',
      paragraphs: [
        'Il titolare può sospendere o chiudere un account e le sue aste in caso di violazione di questi termini, di uso che metta a rischio il servizio o gli altri utenti, oppure di richiesta di un’autorità. Dove possibile lo comunica prima.',
        'Il servizio può cessare in qualunque momento, essendo gratuito e non professionale; in quel caso si darà un preavviso ragionevole tramite questo sito.',
        `Puoi chiudere il tuo account scrivendo a ${LEGAL_HOLDER.email}: i dati verranno cancellati come descritto nell’informativa privacy.`,
      ],
    },
    {
      id: 'proprieta',
      heading: '11. Proprietà',
      paragraphs: [
        'Il software, il nome e il marchio Falasta, il logo e la grafica appartengono al titolare. Nessuna licenza su di essi è concessa dall’uso del servizio.',
        'I dati che carichi restano tuoi: il titolare li tratta soltanto per far funzionare l’asta.',
      ],
    },
    {
      id: 'modifiche',
      heading: '12. Modifiche di questi termini',
      paragraphs: [
        `Questi termini sono nella versione del ${LEGAL_EFFECTIVE_DATE}. La versione accettata al momento della registrazione viene archiviata insieme all’account.`,
        'Se cambia qualcosa di sostanziale, la nuova versione viene pubblicata qui e viene chiesta una nuova accettazione al primo accesso utile. Continuare a usare il servizio dopo la pubblicazione di una modifica di dettaglio significa accettarla.',
      ],
    },
    {
      id: 'legge',
      heading: '13. Legge applicabile e foro competente',
      paragraphs: [
        'Si applica la legge italiana.',
        'Se usi Falasta come consumatore, per le controversie è competente il giudice del luogo in cui risiedi o hai domicilio, come previsto dalle norme inderogabili a tutela del consumatore. Negli altri casi è competente il foro del luogo di residenza del titolare.',
      ],
    },
  ],
};

const PRIVACY: LegalDocument = {
  id: 'privacy',
  path: LEGAL_DOCUMENT_PATHS.privacy,
  title: 'Informativa privacy',
  summary:
    'Quali dati raccoglie Falasta, perché, per quanto tempo e come farli cancellare. Art. 13 GDPR.',
  sections: [
    {
      id: 'titolare',
      heading: '1. Titolare del trattamento',
      paragraphs: [
        `Il titolare del trattamento è ${LEGAL_HOLDER.name} (${LEGAL_HOLDER.description}). Per qualunque richiesta sui tuoi dati, incluso l’esercizio dei diritti descritti al punto 8: ${LEGAL_HOLDER.email}.`,
        'Non è stato nominato un responsabile della protezione dei dati: il trattamento non rientra fra i casi in cui la nomina è obbligatoria.',
      ],
    },
    {
      id: 'dati-amministratore',
      heading: '2. Dati di chi organizza un’asta',
      paragraphs: ['Registrando un account amministratore vengono trattati:'],
      bullets: [
        'nome visualizzato ed email, che sono ciò che serve per accedere e per essere riconosciuti;',
        'la password, conservata solo come impronta crittografica (Argon2id): non esiste alcun percorso, nemmeno per il titolare, che la riporti in chiaro;',
        'la data di registrazione e le date di accesso;',
        'la versione dei documenti legali accettata, il momento dell’accettazione, l’indirizzo IP e il browser dichiarato: è la prova del consenso, e serve al titolare per dimostrare di averlo raccolto;',
        'il contenuto delle aste create: nomi delle aste, liste di calciatori caricate, offerte, budget e rose.',
      ],
    },
    {
      id: 'dati-partecipante',
      heading: '3. Dati di chi partecipa a un’asta',
      paragraphs: [
        'I dati dei fantallenatori — nome ed email — non li raccoglie Falasta: li inserisce l’amministratore dell’asta, che dichiara di essere legittimato a farlo e di aver informato l’interessato (punto 6 dei termini di servizio). Il titolare li tratta per permettere l’accesso all’asta e per farla funzionare.',
        'L’invito per email non contiene una password: contiene un codice monouso di cui nel database esiste solo l’impronta. Il corpo dell’email, che contiene il link in chiaro, viene cancellato subito dopo la consegna.',
        'Durante l’asta vengono registrate le offerte, il budget residuo e i calciatori acquistati: sono il contenuto del gioco e sono visibili agli altri partecipanti della stessa asta.',
      ],
    },
    {
      id: 'dati-tecnici',
      heading: '4. Dati tecnici e misura d’uso',
      paragraphs: [
        'Come qualunque servizio web, i server registrano indirizzo IP, data, richiesta ed esito: serve a far funzionare il servizio, a diagnosticare i guasti e a difendersi dagli abusi.',
        'Con il tuo consenso, e soltanto con esso, viene usato Google Analytics per capire come vengono usate le pagine (quali sezioni si leggono, da quale tipo di dispositivo). Senza consenso nessuno script di Google viene caricato: non parte alcuna richiesta verso i suoi server. I dettagli, cookie per cookie, sono nella cookie policy.',
        'Non c’è pubblicità, non c’è profilazione pubblicitaria, e i segnali pubblicitari di Google sono disattivati.',
      ],
    },
    {
      id: 'basi-giuridiche',
      heading: '5. Perché è lecito trattarli',
      paragraphs: ['Ogni trattamento ha la sua base giuridica:'],
      bullets: [
        'account, aste e invii di invito: esecuzione del servizio che hai richiesto (art. 6.1.b GDPR);',
        'log tecnici, protezione da abusi e sicurezza: legittimo interesse del titolare a mantenere il servizio funzionante e sicuro (art. 6.1.f);',
        'archiviazione dell’accettazione dei documenti: obbligo di poter dimostrare il consenso e legittimo interesse a difendersi in caso di contestazione (artt. 7.1 e 6.1.f);',
        'misura d’uso con Google Analytics: consenso, revocabile in qualunque momento (art. 6.1.a e art. 122 del Codice privacy).',
      ],
    },
    {
      id: 'conservazione',
      heading: '6. Per quanto tempo',
      paragraphs: ['I tempi di conservazione sono questi:'],
      bullets: [
        'dati dell’account e delle aste: finché l’account esiste;',
        `su richiesta di cancellazione: entro ${String(LEGAL_RESPONSE_DAYS)} giorni, salvo quanto debba essere conservato per obbligo di legge;`,
        'corpo delle email di invito: cancellato subito dopo la consegna, perché contiene il link di accesso;',
        'log tecnici: non oltre dodici mesi;',
        'accettazione dei documenti legali: per il tempo in cui può servire come prova, e comunque non oltre dieci anni dalla chiusura dell’account;',
        'preferenza sui cookie: sul tuo dispositivo, finché non la cambi o non svuoti il browser.',
      ],
    },
    {
      id: 'destinatari',
      heading: '7. A chi vengono comunicati',
      paragraphs: [
        'I dati non vengono venduti, ceduti né usati per finalità diverse da quelle descritte qui.',
        'Ne trattano una parte, per conto del titolare e in qualità di responsabili, il fornitore dell’infrastruttura su cui il servizio è ospitato e il servizio usato per la consegna delle email. Con il tuo consenso, i dati di navigazione raccolti dalla misura d’uso sono trattati da Google Ireland Limited.',
        'La misura d’uso può comportare un trasferimento di dati verso gli Stati Uniti, sulla base delle garanzie previste dal capo V del GDPR (decisione di adeguatezza EU-US Data Privacy Framework e clausole contrattuali standard). Se non acconsenti, questo trasferimento non avviene.',
      ],
    },
    {
      id: 'diritti',
      heading: '8. I tuoi diritti',
      paragraphs: [
        'Puoi chiedere in qualunque momento l’accesso ai tuoi dati, la loro rettifica o cancellazione, la limitazione del trattamento, la portabilità, e opporti a un trattamento fondato sul legittimo interesse. Dove il trattamento si basa sul consenso, puoi revocarlo senza che ciò pregiudichi la liceità di quanto avvenuto prima.',
        `Per esercitarli basta scrivere a ${LEGAL_HOLDER.email}: la risposta arriva entro ${String(LEGAL_RESPONSE_DAYS)} giorni. Se sei un fantallenatore invitato da altri, puoi rivolgerti indifferentemente all’amministratore della tua asta o direttamente al titolare.`,
        'Se ritieni che i tuoi dati siano trattati illecitamente puoi proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it) o ricorrere all’autorità giudiziaria.',
      ],
    },
    {
      id: 'automatismi',
      heading: '9. Decisioni automatizzate',
      paragraphs: [
        'L’asta è condotta da un programma: è il server che decide chi ha vinto un calciatore, a che prezzo e quando è scaduto il tempo. Sono decisioni sulle regole del gioco, non sulle persone: non c’è profilazione e non vengono prese decisioni automatizzate che producano effetti giuridici sui tuoi diritti.',
      ],
    },
    {
      id: 'sicurezza',
      heading: '10. Come sono protetti',
      paragraphs: [
        'Le password sono conservate come impronte Argon2id; i codici di invito solo come impronte SHA-256; il traffico è cifrato con TLS; i cookie di sessione non sono leggibili da JavaScript.',
        'Ogni lettura di un’asta è vincolata al suo proprietario a livello di interrogazione del database, e un’asta di un altro amministratore risponde come un’asta inesistente: non è possibile scoprirne l’esistenza tentando indirizzi.',
        'Nessuna misura è perfetta: se sospetti un accesso non autorizzato, scrivi al titolare.',
      ],
    },
    {
      id: 'modifiche-informativa',
      heading: '11. Modifiche',
      paragraphs: [
        `Questa informativa è nella versione del ${LEGAL_EFFECTIVE_DATE}. Le versioni successive vengono pubblicate su questa pagina; se le modifiche riguardano finalità o basi giuridiche, verrà chiesta una nuova accettazione.`,
      ],
    },
  ],
};

const COOKIES: LegalDocument = {
  id: 'cookies',
  path: LEGAL_DOCUMENT_PATHS.cookies,
  title: 'Cookie policy',
  summary: 'Quali cookie usa Falasta, quali no, e come cambiare idea in un clic.',
  sections: [
    {
      id: 'necessari',
      heading: '1. Cookie tecnici, necessari',
      paragraphs: [
        'Servono a far funzionare l’accesso e non richiedono consenso (art. 122 del Codice privacy). Senza di essi non è possibile restare autenticati.',
      ],
      bullets: [
        '«falasta_admin_session»: tiene aperta la sessione dell’amministratore. Durata pari alla sessione configurata (dodici ore), non leggibile da JavaScript.',
        '«falasta_participant_session»: la stessa cosa per il fantallenatore che è entrato dal link di invito.',
        '«falasta.cookie-consent»: non è un cookie ma una voce nella memoria locale del browser, e conserva la scelta che fai qui sotto. Resta sul tuo dispositivo e non viene mai inviata al server.',
      ],
    },
    {
      id: 'misura',
      heading: '2. Cookie di misura d’uso, solo con il tuo consenso',
      paragraphs: [
        'Se acconsenti, viene caricato Google Analytics 4, che aiuta a capire quali pagine vengono lette e da quale tipo di dispositivo. I cookie che imposta sono di Google Ireland Limited:',
      ],
      bullets: [
        '«_ga»: distingue i visitatori. Durata due anni.',
        '«_ga_» seguito dall’identificativo della proprietà: mantiene lo stato della sessione di misura. Durata due anni.',
      ],
    },
    {
      id: 'niente-pubblicita',
      heading: '3. Quello che non c’è',
      paragraphs: [
        'Non ci sono cookie pubblicitari, non c’è profilazione, non ci sono pulsanti social che ti seguono, non ci sono mappe o video di terzi incorporati. I segnali pubblicitari di Google sono disattivati, e le finalità pubblicitarie sono negate anche quando acconsenti alla misura.',
      ],
    },
    {
      id: 'scelta',
      heading: '4. Come si scelgono e come si cambia idea',
      paragraphs: [
        'Alla prima visita compare una striscia con due pulsanti della stessa evidenza: «Accetta» e «Rifiuta». Finché non scegli, nessuno script di Google viene caricato — non parte nemmeno la richiesta per scaricarlo, quindi il tuo indirizzo IP non raggiunge i suoi server. Chiudere la pagina o continuare a leggere non vale come consenso.',
        'Per cambiare idea in qualunque momento c’è «Preferenze cookie» nel piede di ogni pagina, sia qui sia nell’applicazione: la scelta viene azzerata e la striscia ricompare. Se revochi dopo aver accettato, la misura viene disattivata immediatamente.',
        'La scelta viene richiesta di nuovo quando questa pagina cambia in modo sostanziale: un consenso dato su un testo diverso non vale.',
      ],
    },
    {
      id: 'browser',
      heading: '5. Cancellarli dal browser',
      paragraphs: [
        'Puoi cancellare i cookie già presenti e bloccarne di nuovi dalle impostazioni del tuo browser (in Chrome, Firefox, Safari ed Edge la voce si trova sotto «Privacy»). Bloccando i cookie tecnici, però, l’accesso all’asta smette di funzionare: la sessione non può essere mantenuta.',
      ],
    },
  ],
};

export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [TERMS, PRIVACY, COOKIES];

export function legalDocument(id: LegalDocumentId): LegalDocument {
  const found = LEGAL_DOCUMENTS.find((document) => document.id === id);

  if (found === undefined) {
    // Non può succedere: `LegalDocumentId` è un'unione chiusa e il test verifica
    // che ci sia un documento per ciascun valore. L'eccezione serve a non
    // restituire `undefined` a un componente.
    throw new Error(`Documento legale mancante: ${id}`);
  }

  return found;
}

/**
 * Impronta del contenuto dei documenti.
 *
 * Il test la ricalcola e la confronta con `LEGAL_CONTENT_CHECKSUM`: cambiare il
 * testo senza passare da qui è impossibile, e passando da qui la domanda «va
 * alzata la versione?» diventa inevitabile. Volutamente banale — non serve
 * resistere a un attacco, serve accorgersi di una modifica.
 */
export function legalContentChecksum(documents: readonly LegalDocument[]): string {
  const text = documents
    .flatMap((document) => [
      document.id,
      document.title,
      document.summary,
      ...document.sections.flatMap((section) => [
        section.heading,
        ...section.paragraphs,
        ...(section.bullets ?? []),
      ]),
    ])
    .join(' ');

  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return `${hash.toString(16).padStart(8, '0')}-${String(text.length)}`;
}

/**
 * L'impronta dei testi attualmente in vigore, insieme alla versione a cui
 * appartengono. Se il test fallisce: se la modifica è sostanziale alza
 * `LEGAL_VERSION` nei contratti, altrimenti aggiorna solo l'impronta.
 */
export const LEGAL_CONTENT_CHECKSUM = {
  version: LEGAL_VERSION,
  checksum: 'c58502e3-14731',
} as const;
